import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { passwordClipboardProps } from "../utils/passwordClipboard";

import { useWallet } from "../context/WalletContext";
import LoginTwoFactorModal from "../components/LoginTwoFactorModal";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from;
  const postLoginPath = typeof returnTo === "string" && returnTo.startsWith("/") && !returnTo.startsWith("//")
    ? returnTo
    : "/feed";

  const { fetchWallet } = useWallet();

  const [staySignedIn, setStaySignedIn] = useState(false);

  const handleCheckboxChange = (e) => {
    setStaySignedIn(e.target.checked);
  };

  const successMessage = location.state?.successMessage || "";

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [twoFactor, setTwoFactor] = useState(null);
 
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const completeLogin = async (token, user, shouldNavigate = true) => {
    if (!token) throw new Error("Login response did not include a session token.");
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user || {}));

    await fetchWallet(token); // Wallet API call with fresh token

    window.dispatchEvent(new Event("user-updated"));
    window.dispatchEvent(new Event("storage"));
    if (shouldNavigate) navigate(postLoginPath);
  };

  const verifyTwoFactorLogin = async (otp) => {
    if (!twoFactor?.token) {
      setError("Your verification session is missing. Please sign in again.");
      return false;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/2fa/login`,
        { otp, two_factor_token: twoFactor.token },
        {
          headers: {
            "Content-Type": "application/json",
            ...(twoFactor?.token ? { Authorization: `Bearer ${twoFactor.token}` } : {}),
          },
        }
      );

      const { token, user } = response.data;
      if (response.data.success === false || !token) {
        setError(response.data.message || "Verification failed. Please sign in again.");
        return false;
      }
      await completeLogin(token, user, false);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Incorrect Code. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resendTwoFactorCode = async (authMethod) => {
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/2fa/resend`,
        { auth_method: authMethod },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${twoFactor?.token}` } }
      );
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Unable to send a new code. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim() || !formData.password) {
      const message = "Enter your email address and password.";
      setError(message);
      toast.warn(message);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      const message = "Enter a valid email address.";
      setError(message);
      toast.warn(message);
      return;
    }
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/login`,
        {
          useremail: formData.email,
          password: formData.password,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;
      const requiresTwoFactor = Boolean(data?.requires_2fa ?? data?.two_factor_required);

      if (requiresTwoFactor) {
        const twoFactorToken = data?.two_factor_token || data?.temp_token || data?.two_fa_token;
        if (!twoFactorToken) {
          setError("Unable to start verification. Please sign in again.");
          return;
        }
        setTwoFactor({
          token: twoFactorToken,
          method: data?.auth_method || "authenticator",
        });
        return;
      }

      await completeLogin(data.token, data.user);
    } catch (err) {
      console.error("Login Error:", err);
      setError(err.response?.data?.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="wrapper">
      <section className="signup-section login-section d-flex align-items-center py-5 py-md-0">
        <div className="container signup-container">
          <div className="row">
            {/* Left Side */}
            <div className="col-md-6 text-white d-flex align-items-center pe-md-5">
              <div className="pe-md-5 text-center text-md-start w-100">
                <h1 className="fw-bold mb-4">Welcome back! Ready to log in and dive in?</h1>
                <p className="color858c95 ft-18 lh-27 inter">Sign in to unlock all features and manage your accout effortlessly.</p>
              </div>
            </div>

            {/* Right Side */}
            <div className="col-md-6">
              <div className="form-container mt-md-0 d-flex align-items-center justify-content-center mt-5 sm:mt-0">
                <div className="w-100">
                  <h2 className="ft-28 fw-bold mb-1 textred">Sign In</h2>
                  <p className="color858c95 ft-14 lh-27 inter">Your next insight is waiting. Log in to continue.</p>

                  {/* Success Message */}
                  {successMessage && (
                    <div className="alert alert-success text-center" role="alert">
                      {successMessage}
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="alert alert-danger text-center" role="alert">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} noValidate>
                    {/* Email Field */}
                    <div className="form-group">
                      <input
                        type="email"
                        className={`form-control ${formData.email ? "has-value" : ""}`}
                        id="email"
                        name="email"
                        placeholder=" "
                        autoComplete="new-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="email">Email Address</label>
                    </div>

                    {/* Password Field */}
                    <div className="form-group">
                      <input
                        type="password"
                        className={`form-control ${formData.password ? "has-value" : ""}`}
                        id="password"
                        name="password"
                        autoComplete="new-password"
                        placeholder=" "
                        value={formData.password}
                        onChange={handleChange}
                        {...passwordClipboardProps}
                        required
                      />
                      <label htmlFor="password">Password</label>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">

                      <span className="stay-signed-in checkbox-container d-flex gap-3">
                        <input
                          id="persistent"
                          name="persistent"
                          type="checkbox"
                          checked={staySignedIn}
                          onChange={handleCheckboxChange}
                        />
                        <label htmlFor="persistent" className="ft-12 color858c95">
                          Keep me sign in
                        </label>
                      </span>

                      <div className="form-group my-3">
                        <Link to="/forgot-password" className="form-check-label ft-12 lh-24 graytext">
                          Forget Password?
                        </Link>
                      </div>
                    </div>


                    <button type="submit" className="btn btn-red ft-15 fw-600 w-100 my-3 py-3" disabled={loading}>
                      {loading ? "Signing In..." : "Sign In"} <i className="fa-solid fa-arrow-right ms-2"></i>
                    </button>

                    <p className="mt-3 ft-14 lh-24 text-center">
                      Just starting with I&P?
                      <Link to="/signup" className="Register-link textred" style={{ marginLeft: "5px" }}>
                        Sign Up
                      </Link>
                    </p>


                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {twoFactor && (
        <LoginTwoFactorModal
          initialMethod={twoFactor.method}
          email={formData.email}
          submitting={loading}
          error={error}
          onVerify={verifyTwoFactorLogin}
          onResend={resendTwoFactorCode}
          onClose={() => { if (!loading) { setTwoFactor(null); setError(""); } }}
          onComplete={() => navigate(postLoginPath)}
        />
      )}
    </div>
  );
};

export default Login;
